import React, { useCallback, useState } from 'react';
import { Button, Modal, Form } from 'react-bootstrap';
import { APIProvider, Map, AdvancedMarker, useMapsLibrary } from '@vis.gl/react-google-maps';

interface LocationPickerModalProps {
  show: boolean;
  onHide: () => void;
  onLocationSelected: (lat: number, lng: number) => void;
  apiKey: string;
  initialLat?: number;
  initialLng?: number;
  title?: string;
}

/**
 * LocationPickerModal - A modal that embeds Google Maps to allow users to pick a location
 *
 * Features:
 * - Click on map to select location
 * - Draggable marker
 * - Shows selected coordinates
 * - Search box for location lookup
 *
 * Usage:
 * ```tsx
 * <LocationPickerModal
 *   show={showModal}
 *   onHide={() => setShowModal(false)}
 *   onLocationSelected={(lat, lng) => {
 *     setFieldValue('latitude', lat);
 *     setFieldValue('longitude', lng);
 *     setShowModal(false);
 *   }}
 *   initialLat={formValues.latitude}
 *   initialLng={formValues.longitude}
 * />
 * ```
 */
export const LocationPickerModal: React.FC<LocationPickerModalProps> = ({
  show,
  onHide,
  onLocationSelected,
  apiKey,
  initialLat = 51.5074, // Default to London
  initialLng = -0.1278,
  title = 'Select Location',
}) => {
  const [selectedPosition, setSelectedPosition] = useState<{ lat: number; lng: number }>({
    lat: initialLat,
    lng: initialLng,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [mapCenter, setMapCenter] = useState({ lat: initialLat, lng: initialLng });

  // Update state when modal opens or coordinates change
  React.useEffect(() => {
    if (show) {
      setSelectedPosition({ lat: initialLat, lng: initialLng });
      setMapCenter({ lat: initialLat, lng: initialLng });
    }
  }, [show, initialLat, initialLng]);

  const handleMapClick = useCallback((event: any) => {
    const lat = event.detail?.latLng?.lat;
    const lng = event.detail?.latLng?.lng;
    if (lat !== undefined && lng !== undefined) {
      setSelectedPosition({ lat, lng });
    }
  }, []);

  const handleMarkerDrag = useCallback((event: any) => {
    const lat = event.latLng?.lat();
    const lng = event.latLng?.lng();
    if (lat !== undefined && lng !== undefined) {
      setSelectedPosition({ lat, lng });
    }
  }, []);

  const handleSearch = async (geocodingLib: google.maps.GeocodingLibrary | null) => {
    if (!searchQuery.trim()) return;
    if (!geocodingLib) {
      alert('Geocoding library not loaded yet. Please try again.');
      return;
    }

    try {
      const geocoder = new geocodingLib.Geocoder();
      const result = await geocoder.geocode({ address: searchQuery });

      if (result.results && result.results.length > 0) {
        const location = result.results[0].geometry.location;
        const lat = location.lat();
        const lng = location.lng();
        setSelectedPosition({ lat, lng });
        setMapCenter({ lat, lng });
      } else {
        alert('Location not found. Please try a different search term.');
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      alert('Failed to search for location. Please try again.');
    }
  };

  const handleConfirm = () => {
    onLocationSelected(selectedPosition.lat, selectedPosition.lng);
    onHide();
  };

  const updateLocation = (lat: number, lng: number) => {
    setSelectedPosition({ lat, lng });
    setMapCenter({ lat, lng });
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" fullscreen="md-down">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {apiKey ? (
          <APIProvider apiKey={apiKey}>
            <MapContent
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              selectedPosition={selectedPosition}
              mapCenter={mapCenter}
              handleMapClick={handleMapClick}
              handleMarkerDrag={handleMarkerDrag}
              handleSearch={handleSearch}
              updateLocation={updateLocation}
            />
          </APIProvider>
        ) : (
          <div className="alert alert-warning">
            Google Maps API key not configured. Please set GOOGLE_MAPS_API_KEY in your environment variables.
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleConfirm}>
          Confirm Location
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

interface MapContentProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  selectedPosition: { lat: number; lng: number };
  mapCenter: { lat: number; lng: number };
  handleMapClick: (event: any) => void;
  handleMarkerDrag: (event: any) => void;
  handleSearch: (geocodingLib: google.maps.GeocodingLibrary | null) => void;
  updateLocation: (lat: number, lng: number) => void;
}

const MapContent: React.FC<MapContentProps> = ({
  searchQuery,
  setSearchQuery,
  selectedPosition,
  mapCenter,
  handleMapClick,
  handleMarkerDrag,
  handleSearch,
  updateLocation,
}) => {
  const geocodingLib = useMapsLibrary('geocoding');
  const [searchResults, setSearchResults] = React.useState<google.maps.GeocoderResult[]>([]);
  const [showResults, setShowResults] = React.useState(false);
  const [isSearching, setIsSearching] = React.useState(false);
  const [lastSearchMessage, setLastSearchMessage] = React.useState<string>('');

  const performSearch = async () => {
    if (!searchQuery.trim()) return;
    if (!geocodingLib) {
      setLastSearchMessage('Geocoding library not loaded yet. Please try again.');
      return;
    }

    setIsSearching(true);
    setLastSearchMessage('');

    try {
      const geocoder = new geocodingLib.Geocoder();
      const result = await geocoder.geocode({ address: searchQuery });

      if (result.results && result.results.length > 0) {
        setSearchResults(result.results.slice(0, 5)); // Show top 5 results
        setShowResults(true);
        setLastSearchMessage(`Found ${result.results.length} result(s). Click one to select.`);
      } else {
        setSearchResults([]);
        setShowResults(false);
        setLastSearchMessage('No locations found. Please try a different search term.');
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      setLastSearchMessage('Failed to search for location. Please try again.');
      setSearchResults([]);
      setShowResults(false);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectResult = (result: google.maps.GeocoderResult) => {
    const location = result.geometry.location;
    const lat = location.lat();
    const lng = location.lng();

    // Pass to parent handler which updates selectedPosition and mapCenter
    handleSearch(geocodingLib);

    // But we need to do this locally to update the map view
    const newPosition = { lat, lng };
    setSearchQuery(result.formatted_address);
    setShowResults(false);
    setLastSearchMessage(`✓ Selected: ${result.formatted_address}`);

    // Update parent state by creating a mock event
    handleMapClick({ detail: { latLng: { lat, lng } } });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      performSearch();
    }
  };

  return (
    <>
      {/* Search Box */}
      <Form.Group className="mb-3">
        <Form.Label>Search for a location</Form.Label>
        <div className="d-flex gap-2">
          <div style={{ position: 'relative', flex: 1 }}>
            <Form.Control
              type="text"
              placeholder="Enter address, city, or place name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => searchResults.length > 0 && setShowResults(true)}
            />

            {/* Search Results Dropdown */}
            {showResults && searchResults.length > 0 && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                }}
              >
                {searchResults.map((result, index) => (
                  <div
                    key={index}
                    onClick={() => handleSelectResult(result)}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      borderBottom: index < searchResults.length - 1 ? '1px solid #eee' : 'none',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                  >
                    <div style={{ fontWeight: 500 }}>{result.formatted_address}</div>
                    {result.types && result.types.length > 0 && (
                      <small style={{ color: '#666' }}>
                        {result.types[0].replace(/_/g, ' ')}
                      </small>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <Button variant="primary" onClick={performSearch} disabled={isSearching}>
            {isSearching ? 'Searching...' : 'Search'}
          </Button>
        </div>

        {/* Search Feedback Message */}
        {lastSearchMessage && (
          <small className={lastSearchMessage.includes('✓') ? 'text-success' : 'text-muted'}>
            {lastSearchMessage}
          </small>
        )}
      </Form.Group>

      {/* Current Coordinates Display */}
      <div className="mb-3">
        <small className="text-muted">
          Selected: Latitude {selectedPosition.lat.toFixed(6)}, Longitude {selectedPosition.lng.toFixed(6)}
        </small>
      </div>

      {/* Google Map */}
      <div style={{ height: '400px', width: '100%', position: 'relative' }}>
        <Map
          defaultCenter={mapCenter}
          defaultZoom={13}
          onClick={handleMapClick}
          mapId="location-picker-map"
          gestureHandling="greedy"
          disableDefaultUI={false}
          clickableIcons={false}
          style={{ width: '100%', height: '100%' }}
        >
          <AdvancedMarker position={selectedPosition} draggable={true} onDragEnd={handleMarkerDrag} />
        </Map>
      </div>

      <div className="mt-3">
        <small className="text-muted">
          <strong>Tip:</strong> Click anywhere on the map or drag the marker to select a location. You can also search for
          a specific address above.
        </small>
      </div>
    </>
  );
};
